import { useMemo } from "react";

import {
  getDeploymentChecklistProgress,
  getGetDeploymentChecklistProgressQueryKey,
  markChecklistItemAsCompleted,
  markChecklistItemAsIncomplete,
} from "@/api/orval/ageless-marketing/ageless-marketing";
import {
  getSetupLinkDeploymentChecklistProgress,
  markSetupLinkChecklistItemAsCompleted,
  markSetupLinkChecklistItemAsIncomplete,
} from "@/api/orval/ageless-marketing-setup-link/ageless-marketing-setup-link";
import type { AgelessMarketingDeploymentChecklistProgressForPlatformRetrievedResponse } from "@/api/orval/model/agelessMarketingDeploymentChecklistProgressForPlatformRetrievedResponse";

import { useAgelessMarketingSetupAccess } from "./useAgelessMarketingSetupAccess";

export type AgelessDeploymentChecklistProgress =
  AgelessMarketingDeploymentChecklistProgressForPlatformRetrievedResponse;

export type AgelessDeploymentChecklistApi = {
  /** Distinct per viewer kind, so an admin's cache never satisfies a share-link read or vice versa. */
  readonly queryKey: readonly unknown[];
  readonly fetchProgress: () => Promise<AgelessDeploymentChecklistProgress>;
  readonly setItemCompletion: (itemSlug: string, isCompleted: boolean) => Promise<unknown>;
  /** False when nobody may read or write the checklist — a visitor with no valid setup link. */
  readonly isAvailable: boolean;
};

/**
 * The deployment-checklist API for the current viewer: which endpoints to call and under which
 * query key.
 *
 * A signed-in admin uses the admin endpoints. A visitor holding a valid setup link uses the
 * token-scoped mirror — the practice's web developer is the person actually performing the
 * deployment, so they are the one who can honestly tick the items off. A visitor with no
 * valid link gets no API at all.
 */
export function useAgelessDeploymentChecklistApi(
  platformId: string,
): AgelessDeploymentChecklistApi {
  const { isPublicView, canEditEmbedSettings, setupToken } = useAgelessMarketingSetupAccess();

  return useMemo(() => {
    if (setupToken) {
      return {
        queryKey: ["ageless-marketing-deployment-checklist", "setup-link", platformId],
        // The mutator sends the setup token from the URL whenever this page has no session.
        fetchProgress: () => getSetupLinkDeploymentChecklistProgress(),
        setItemCompletion: (itemSlug: string, isCompleted: boolean) =>
          isCompleted
            ? markSetupLinkChecklistItemAsCompleted(itemSlug)
            : markSetupLinkChecklistItemAsIncomplete(itemSlug),
        isAvailable: true,
      };
    }

    return {
      queryKey: getGetDeploymentChecklistProgressQueryKey(platformId),
      fetchProgress: () => getDeploymentChecklistProgress(platformId),
      setItemCompletion: (itemSlug: string, isCompleted: boolean) =>
        isCompleted
          ? markChecklistItemAsCompleted(platformId, itemSlug)
          : markChecklistItemAsIncomplete(platformId, itemSlug),
      // Signed in, or a share-link holder whose token is still being verified.
      isAvailable: !isPublicView || canEditEmbedSettings,
    };
  }, [canEditEmbedSettings, isPublicView, platformId, setupToken]);
}
